import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Race } from '../types';
import ResultsHistoryView from './results/ResultsHistoryView';
import TrackRecordTeaser from './TrackRecordTeaser';
import LandingFooter from './LandingFooter';
import TrialCta from './TrialCta';
import LoadingSpinner from './LoadingSpinner';
import './LandingPage.css';
import {
  fetchTrackRecordRaces,
  fetchTrackRecordSummary,
  TrackRecordSummary,
  verifyTrackRecordAccess,
} from '../services/trackRecordService';
import './TrackRecordTeaser.css';

const PAGE_SIZE = 100;

const TrackRecordSiteHeader: React.FC = () => (
  <header className="landing-nav track-record-nav">
    <div className="landing-nav-inner container">
      <Link to="/" className="landing-nav-brand" aria-label="GallopAI home">
        <img src="/white_wide_logo_noBG.png" alt="GallopAI" className="landing-nav-logo" />
      </Link>
      <p className="track-record-nav-tagline">
        Want today&apos;s picks and optional Betfair autopilot?
      </p>
      <div className="landing-nav-actions">
        <Link to="/login" className="btn btn-signin">
          Sign In
        </Link>
        <TrialCta className="btn-compact-trial">Start your free trial</TrialCta>
      </div>
    </div>
  </header>
);

type PageShellProps = {
  children: React.ReactNode;
  helmet?: React.ReactNode;
};

/** Same flex shell as authenticated app routes: full-width chrome, constrained main. */
const TrackRecordPageShell: React.FC<PageShellProps> = ({ children, helmet }) => (
  <div className="public-track-record-page">
    {helmet}
    <TrackRecordSiteHeader />
    <main className="app-main">{children}</main>
    <LandingFooter />
  </div>
);

const PublicTrackRecordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const access = searchParams.get('access')?.trim() || '';
  const src = searchParams.get('src')?.trim() || undefined;

  const [tokenValid, setTokenValid] = useState<boolean | null>(null);
  const [races, setRaces] = useState<Race[]>([]);
  const [globalSummary, setGlobalSummary] = useState<TrackRecordSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [totalFiltered, setTotalFiltered] = useState(0);

  const loadFirstPage = useCallback(async () => {
    if (!access) return;
    setLoading(true);
    setError(null);
    try {
      const [page, summary] = await Promise.all([
        fetchTrackRecordRaces(access, {
          paginated: true,
          limit: PAGE_SIZE,
        }),
        fetchTrackRecordSummary(),
      ]);
      setRaces(page.items);
      setNextCursor(page.nextCursor);
      setTotalFiltered(page.totalFiltered);
      setGlobalSummary(summary);
    } catch (err) {
      if (err instanceof Error && err.message === 'ACCESS_EXPIRED') {
        setTokenValid(false);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load track record');
      }
    } finally {
      setLoading(false);
    }
  }, [access]);

  useEffect(() => {
    if (!access) {
      setTokenValid(false);
      return;
    }
    let cancelled = false;
    verifyTrackRecordAccess(access).then((result) => {
      if (!cancelled) {
        setTokenValid(result.valid);
        if (result.valid) {
          void loadFirstPage();
        }
      }
    });
    return () => {
      cancelled = true;
    };
  }, [access, loadFirstPage]);

  const loadMore = useCallback(async () => {
    if (!access || !nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await fetchTrackRecordRaces(access, {
        paginated: true,
        cursor: nextCursor,
        limit: PAGE_SIZE,
      });
      setRaces((prev) => [...prev, ...page.items]);
      setNextCursor(page.nextCursor);
      setTotalFiltered(page.totalFiltered);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  }, [access, nextCursor, loadingMore]);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'GallopAI Track Record — UK Horse Racing AI Results History',
    description:
      'View settled GallopAI picks and results for UK horse racing through yesterday. Past performance is not indicative of future results.',
    url: 'https://gallopai.app/track-record',
    isPartOf: {
      '@type': 'WebSite',
      name: 'GallopAI',
      url: 'https://gallopai.app/',
    },
  };

  if (!access || tokenValid === false) {
    return (
      <TrackRecordPageShell
        helmet={
          <Helmet>
            <title>GallopAI Track Record — UK Racing AI Results History</title>
            <meta
              name="description"
              content="Request the full GallopAI Results History: settled AI picks, win rate, and P/L through yesterday. UK horse racing."
            />
            <meta property="og:title" content="GallopAI Track Record — Results History" />
            <meta
              property="og:description"
              content="Settled AI picks and outcomes for UK horse racing. Email yourself the full interactive history."
            />
            <meta property="og:url" content="https://gallopai.app/track-record" />
            <meta property="og:type" content="website" />
            <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
          </Helmet>
        }
      >
        <div className="public-track-record-seo">
          <h1>GallopAI track record</h1>
          <p>
            See how our AI picks have performed on settled UK races through yesterday. Enter your email and we&apos;ll
            send you a link to the full Results History with filters and charts.
          </p>
          <p className="public-track-record-disclaimer">
            Past performance is not indicative of future results. P/L uses suggested stake points for illustration only.
          </p>
        </div>

        <TrackRecordTeaser variant="page" source={src ?? 'page'} />

        <div className="public-track-record-gate">
          <p>
            <Link to="/#track-record">Request access from the homepage</Link> or use the form above.
          </p>
          <p style={{ marginTop: '1rem' }}>
            <Link to="/start-checkout/betfair">Start your free trial</Link> for today&apos;s picks and optional Betfair autopilot.
          </p>
        </div>
      </TrackRecordPageShell>
    );
  }

  if (tokenValid === null || (loading && races.length === 0)) {
    return (
      <TrackRecordPageShell>
        <LoadingSpinner />
      </TrackRecordPageShell>
    );
  }

  const header = (
    <div className="results-header">
      <h1>GallopAI track record</h1>
      <p>
        Complete Results History for our system&apos;s settled picks through yesterday (UK time). P/L figures use
        suggested stake points for illustration only.
      </p>
      <p className="public-track-record-disclaimer">
        Past performance is not indicative of future results. Never bet more than you can afford to lose.
      </p>
    </div>
  );

  return (
    <TrackRecordPageShell
      helmet={
        <Helmet>
          <title>Your GallopAI Results History</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
      }
    >
      <ResultsHistoryView
        races={races}
        loading={loading && races.length === 0}
        error={error}
        onRetry={loadFirstPage}
        header={header}
        showSubscriberLinks={false}
        onLoadMore={nextCursor ? loadMore : undefined}
        loadingMore={loadingMore}
        hasMoreRemote={!!nextCursor}
        totalRemoteCount={totalFiltered}
        globalSummary={globalSummary}
        globalChartData={globalSummary?.charts ?? null}
      />
    </TrackRecordPageShell>
  );
};

export default PublicTrackRecordPage;
